import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import {defaultFilters} from '@/models/GeneralReport/helpers'
import TableOptions from '@/models/TableOptions'
import {getPaginatedList, getSortedItems, mockPagination, omitEmptyDeepFilters, prepareFilters} from '@/helpers/filters'
import playerReportsRepository from "@/repository/generatedRepository/playerReportsRepository";
import {generatePassword} from "@/helpers/functions";
import {
  defineGroupId,
  checkSortBy,
  prepareExportReportData,
  filterBySpend,
  toApiGroupBy,
  applyGroupFieldFormat,
  prepareNotUsedSpend,
  transformSubRow,
  applySegmentFilter,
} from '@/helpers/general-report'
import { canFitInLocalStorage } from '@/helpers/reports'

const state = {
  list: [],
  listReport: [],
  paginatedList: [],
  notUsedSpend: [],
  total: {},
  playersCount: 0,
  filters: StorageService.get(storageKeys.GENERAL_REPORT_FILTERS) || {...defaultFilters()},
  tableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  tableOptions: state => state.tableOptions,
  total: state => state.total,
  list: state => state.list,
  listReport: state => state.listReport,
  playersCount: state => state.playersCount,
  notUsedSpend: state => state.notUsedSpend,
  paginatedList: state => state.paginatedList,
  listPrepared: state => {
    const sorted = getSortedItems(
      state.list,
      { sortBy: state.filters?.sortBy1, sortDesc: state.filters?.sortDesc1 },
      'generalReport',
    )
    const list = state.filters?.filter?.spend === 'only_with_spend'
      ? filterBySpend(sorted, state.filters.filter)
      : sorted
    return [...list, ...state.notUsedSpend]
  },
  listPaginated: state => {
    const paginated = getPaginatedList(
      state.paginatedList,
      state.tableOptions.pagination,
      { sortBy: state.filters.sortBy1, sortDesc: state.filters.sortDesc1 },
      'generalReport',
    )
    const list = state.filters?.filter?.spend === 'only_with_spend'
      ? filterBySpend(paginated, state.filters.filter)
      : paginated
    return [
      ...list,
      ...(state.tableOptions.page === state.tableOptions.totalItems ? state.notUsedSpend : []),
    ]
  },
  isOpenFilterPanel: state => state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
}

const actions = {
  async loadData(context, filtersData) {
    const {updateData = true, signal, ...filters} = filtersData
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.GENERAL_REPORT_FILTERS, filters)
    const appliedFilters = applySegmentFilter(omitEmptyDeepFilters(filters.filter))
    delete appliedFilters.spend

    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
    })
    let data = {}
    if (StorageService.get(storageKeys.GENERAL_REPORT_DATA) && !updateData) {
      data = StorageService.get(storageKeys.GENERAL_REPORT_DATA)
    } else {
      const apiFilter = {
        ...preparedFilters.filter,
        groupBy: toApiGroupBy(preparedFilters.filter.groupBy),
        secondGroupBy: toApiGroupBy(preparedFilters.filter.secondGroupBy),
        thirdGroupBy: toApiGroupBy(preparedFilters.filter.thirdGroupBy),
        fourthGroupBy: toApiGroupBy(preparedFilters.filter.fourthGroupBy),
      }
      data = (await playerReportsRepository.generalReport({ ...apiFilter, signal })).data
    }
    if (canFitInLocalStorage(data)) {
      StorageService.set(storageKeys.GENERAL_REPORT_DATA, data)
    } else {
      StorageService.remove(storageKeys.GENERAL_REPORT_DATA)
    }
    context.commit('setReportData', {data, filters})
    context.commit('setList', { data, filters })
    context.commit('setPaginatedList', { data, pagination: mockPagination(data.rows, filters), filters })
    const sortOptions = checkSortBy(filters)
    if (sortOptions && ['date', 'week', 'month'].includes(filters.filter.groupBy)) {
      context.dispatch('updateSortGeneralReport', { ...filters, ...sortOptions })
    }
    return {products: data.items}
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.GENERAL_REPORT_FILTERS)
  },
  updateSpendFilter({ commit, getters }, spend) {
    const updatedFilters = {
      ...getters.filters,
      filter: { ...getters.filters.filter, spend },
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.GENERAL_REPORT_FILTERS, updatedFilters)
  },
  updateFiltersClientOptions({commit, getters}, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.GENERAL_REPORT_FILTERS, updatedFilters)
  },
  updatePaginationGeneralReport(context, filters) {
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.GENERAL_REPORT_FILTERS, filters)
    context.commit('setPagination', filters.pagination)
  },
  updateSortGeneralReport(context, filters) {
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.GENERAL_REPORT_FILTERS, filters)
    context.commit('setSort', {
      sortBy: filters.sortBy,
      sortDesc: filters.sortDesc,
    })
  }
}

const mutations = {
  setList(state, {data, pagination, filters}) {
    if (data === null) return (state.list = [])
    const firstGroupBy = filters.filter.groupBy
    const secondGroupBy = filters.filter.secondGroupBy
    const thirdGroupBy = filters.filter.thirdGroupBy
    const fourthGroupBy = filters.filter.fourthGroupBy

    const convArr = prepareNotUsedSpend(data.not_used_spend, firstGroupBy, secondGroupBy)

    const subIds = new Set()
    data.rows.forEach(row => {
      row.id = generatePassword(8)
      if (defineGroupId(secondGroupBy) !== defineGroupId(firstGroupBy))
        row.g[defineGroupId(secondGroupBy)] = null
      if (defineGroupId(thirdGroupBy) !== defineGroupId(firstGroupBy))
        row.g[defineGroupId(thirdGroupBy)] = null
      if (defineGroupId(fourthGroupBy) !== defineGroupId(firstGroupBy))
        row.g[defineGroupId(fourthGroupBy)] = null
      if ([defineGroupId(secondGroupBy), defineGroupId(thirdGroupBy), defineGroupId(fourthGroupBy)].includes('player_id'))
        row.g.click_id = null
      applyGroupFieldFormat(row, firstGroupBy, secondGroupBy)
      if (firstGroupBy === 'sub_id') Object.keys(row.g.sub_ids).forEach(key => subIds.add(key))

      if (secondGroupBy) row.sg.forEach(subRow => {
        transformSubRow(subRow, secondGroupBy, [firstGroupBy, thirdGroupBy, fourthGroupBy], [thirdGroupBy, fourthGroupBy], subIds)
        if (thirdGroupBy) subRow.tg.forEach(item => {
          transformSubRow(item, thirdGroupBy, [firstGroupBy, secondGroupBy, fourthGroupBy], [fourthGroupBy], subIds, { trafficSuffix: true })
          if (fourthGroupBy) item.fg.forEach(subItem =>
            transformSubRow(subItem, fourthGroupBy, [firstGroupBy, secondGroupBy, thirdGroupBy], [], subIds, { trafficSuffix: true })
          )
        })
      })
    })
    state.list = [...data.rows]
    state.notUsedSpend = Object.values(data.not_used_spend).length ? [convArr] : []
    data.total.sub_ids_headers = Array.from(subIds)
    state.total = data.total
    state.playersCount = data.player_count
    state.tableOptions = new TableOptions(pagination, filters)
  },
  setReportData(state, {data, filters}){ // for report
    const reportData = prepareExportReportData(data, filters)
    state.listReport = [...reportData]
  },
  setPaginatedList(state, {data, pagination, filters}) {
    state.paginatedList = [...data.rows]
    state.total = data.total
    state.tableOptions = new TableOptions(pagination, filters)
  },
  setFilters(state, filters) {
    state.filters = {...filters}
  },
  setPagination(state, pagination) {
    state.tableOptions = {
      ...state.tableOptions,
      page: pagination.page,
      pagination,
      totalItems: Math.ceil(state.tableOptions.total / pagination.limit),
    }
  },
  setSort(state, {sortBy, sortDesc}) {
    state.tableOptions = {
      ...state.tableOptions,
      sortBy,
      sortDesc,
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
